// Add this component to handle formatted AI responses
const FormattedAIResponse = ({ content }) => {
  // Parse emojis and format the response
  const formatResponse = (text) => {
    if (!text) return text;

    // Check if this is a bullet-separated summary (like "✅ **10 Records** • 💰 **$500**")
    if (text.includes(' • ') && (text.includes('✅') || text.includes('💰') || text.includes('📊'))) {
      const bulletParts = text.split(' • ');
      return bulletParts.map(part => {
        const emojiMatch = part.match(/^([\u{1F300}-\u{1F9FF}]|✅|❌|🟡|📋|💰|📊|📄|🛣️|⚡)\s*(.*)$/u);
        if (emojiMatch) {
          const [, emoji, textContent] = emojiMatch;
          return { type: 'summary-item', emoji, text: textContent };
        }
        return { type: 'summary-item', emoji: '✅', text: part };
      });
    }

    // Split by common patterns to identify different parts
    const parts = [];
    const lines = text.split('\n');

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      // Check if line starts with emoji or status indicator
      const emojiMatch = line.match(/^([\u{1F300}-\u{1F9FF}]|✅|❌|🟡|📋|💰|📊|📄|🛣️|⚡)\s*(.*)$/u);

      if (emojiMatch) {
        const [, emoji, text] = emojiMatch;
        parts.push({ type: 'status', emoji, text });
      } else if (line.includes('$') && /\$[\d,]+\.?\d*/.test(line)) {
        // Line contains monetary amounts
        parts.push({ type: 'financial', text: line });
      } else if (line.toLowerCase().includes('yes') || line.toLowerCase().includes('no')) {
        // Yes/No responses
        parts.push({ type: 'answer', text: line });
      } else if (line.startsWith('•') || line.startsWith('-')) {
        // Bullet points
        parts.push({ type: 'bullet', text: line });
      } else {
        // Regular text
        parts.push({ type: 'text', text: line });
      }
    }

    return parts.length > 0 ? parts : [{ type: 'text', text }];
  };

  const formatBold = (text) => {
    // Format **bold** markdown
    return text.replace(/\*\*([^*]+)\*\*/g, (match, content) => {
      return `<strong>${content}</strong>`;
    });
  };

  const formatMoney = (text) => {
    return text.replace(/\$(\d+(?:,\d{3})*(?:\.\d{2})?)/g, (match, amount) => {
      return `<span class="money-value">$${amount}</span>`;
    });
  };

  const formatDates = (text) => {
    // Format date patterns
    return text.replace(/\b(\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2})\b/g, (match) => {
      return `<span class="date-value">${match}</span>`;
    });
  };

  const formatInvoiceNumbers = (text) => {
    // Format invoice numbers
    return text.replace(/\b(invoice\s+(?:number\s+)?)([\w\d-]+)\b/gi, (match, prefix, number) => {
      return `${prefix}<span class="invoice-number">${number}</span>`;
    });
  };

  // Format numbers (non-currency) with green styling
  const formatNumbers = (text) => {
    // Format standalone numbers that aren't part of dates or money
    return text.replace(/\b(\d{1,3}(?:,\d{3})*)\b(?!\s*(?:\/|\$|%))/g, (match, number) => {
      // Skip if it looks like a year or small number in context
      if (parseInt(number.replace(/,/g, '')) < 10) return match;
      return `<span class="number-value">${number}</span>`;
    });
  };

  const parts = formatResponse(content);

  // Check if we have summary items (bullet-separated format)
  const isSummaryFormat = parts.length > 0 && parts[0].type === 'summary-item';

  if (isSummaryFormat) {
    return (
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.75rem',
        padding: '1rem',
        backgroundColor: '#f0fdf4',
        borderRadius: '12px',
        border: '2px solid #22c55e'
      }}>
        {parts.map((part, index) => {
          let formattedText = part.text;
          formattedText = formatBold(formattedText);
          formattedText = formatMoney(formattedText);
          formattedText = formatNumbers(formattedText);

          return (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #bbf7d0',
              boxShadow: '0 1px 3px rgba(34, 197, 94, 0.1)'
            }}>
              <span style={{ fontSize: '1.25rem' }}>{part.emoji}</span>
              <div
                style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#065f46'
                }}
                dangerouslySetInnerHTML={{ __html: formattedText }}
              />
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div style={{ lineHeight: '1.6' }}>
      {parts.map((part, index) => {
        let formattedText = part.text;
        formattedText = formatBold(formattedText);
        formattedText = formatMoney(formattedText);
        formattedText = formatDates(formattedText);
        formattedText = formatInvoiceNumbers(formattedText);
        formattedText = formatNumbers(formattedText);

        switch (part.type) {
          case 'status':
            return (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                marginBottom: '0.75rem',
                padding: '0.75rem',
                backgroundColor: '#f0fdf4',
                borderRadius: '8px',
                border: '1px solid #22c55e'
              }}>
                <span style={{
                  fontSize: '1.25rem',
                  lineHeight: '1',
                  minWidth: '1.5rem'
                }}>
                  {part.emoji}
                </span>
                <div
                  style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#065f46',
                    flex: 1
                  }}
                  dangerouslySetInnerHTML={{ __html: formattedText }}
                />
              </div>
            );
          
          case 'financial':
            return (
              <div key={index} style={{
                marginBottom: '0.75rem',
                padding: '0.75rem',
                backgroundColor: '#f0fdf4',
                borderRadius: '8px',
                border: '1px solid #bbf7d0'
              }}>
                <div 
                  style={{ 
                    fontSize: '1rem', 
                    fontWeight: '500',
                    color: '#065f46'
                  }}
                  dangerouslySetInnerHTML={{ __html: formattedText }}
                />
              </div>
            );
          
          case 'answer':
            return (
              <div key={index} style={{
                marginBottom: '0.75rem',
                padding: '0.75rem',
                backgroundColor: part.text.toLowerCase().includes('yes') ? '#f0fdf4' : '#fef2f2',
                borderRadius: '8px',
                border: `1px solid ${part.text.toLowerCase().includes('yes') ? '#bbf7d0' : '#fecaca'}`
              }}>
                <div 
                  style={{ 
                    fontSize: '1.1rem', 
                    fontWeight: '600',
                    color: part.text.toLowerCase().includes('yes') ? '#065f46' : '#991b1b'
                  }}
                  dangerouslySetInnerHTML={{ __html: formattedText }}
                />
              </div>
            );
          
          case 'bullet':
            return (
              <div key={index} style={{ 
                marginBottom: '0.5rem',
                marginLeft: '1rem'
              }}>
                <div 
                  style={{ 
                    fontSize: '0.95rem', 
                    color: '#475569',
                    lineHeight: '1.6'
                  }}
                  dangerouslySetInnerHTML={{ __html: formattedText }}
                />
              </div>
            );
          
          default:
            return (
              <div key={index} style={{ marginBottom: '0.5rem' }}>
                <div 
                  style={{ 
                    fontSize: '1rem', 
                    color: '#374151',
                    lineHeight: '1.6'
                  }}
                  dangerouslySetInnerHTML={{ __html: formattedText }}
                />
              </div>
            );
        }
      })}
    </div>
  );
};

export default FormattedAIResponse;